import * as React from 'react';
import MuiAppBar from '@mui/material/AppBar';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Slide from '@mui/material/Slide';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import * as Device from 'react-device-detect';

import { Logo, AccountHeader } from '@components';
import { NavTabs } from './NavTabs';
import { MobileSearchHeader } from './MobileSearchHeader';
import { MobileCalendarHeader } from './MobileCalendarHeader';

interface AppBarProps {
  scrollRef?: React.RefObject<HTMLElement>;
}

interface HideOnScrollProps {
  children?: React.ReactElement;
  show?: boolean;
}

function HideOnScroll({ children, show }: HideOnScrollProps) {
  return !Capacitor.isNativePlatform() && Device.isMobile ? (
    <Slide appear direction="down" in={show}>
      {children ?? <div />}
    </Slide>
  ) : (
    children
  );
}

export function AppBar(props: Readonly<AppBarProps>) {
  const { scrollRef } = props;
  const lastScrollTop = React.useRef(0);
  const [show, setShow] = React.useState(true);

  React.useEffect(() => {
    const target = scrollRef?.current;
    if (!target) return;

    const onScroll = () => {
      const current = target.scrollTop;
      const isScrollingUp = current < lastScrollTop.current;

      setShow(isScrollingUp || current < 10);
      lastScrollTop.current = current;
    };

    target.addEventListener('scroll', onScroll);
    return () => target.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  return (
    <HideOnScroll show={show}>
      <MuiAppBar id="appbar" position="fixed" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <RenderToolbar />
        </Toolbar>
      </MuiAppBar>
    </HideOnScroll>
  );
}

const RegisterToolbar = () => {
  return (
    <>
      <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center">
        <Logo variant="full" size="large" />
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        <AccountHeader />
      </Stack>
    </>
  );
};

const DefaultToolbar = () => {
  return (
    <>
      <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center">
        <Logo variant="full" size="large" />
        <NavTabs />
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        <AccountHeader />
      </Stack>
    </>
  );
};

const RenderToolbar = () => {
  const { pathname } = useLocation();

  if (Device.isDesktop) {
    if (pathname.startsWith('/register')) {
      return <RegisterToolbar />;
    }

    return <DefaultToolbar />;
  }

  if (pathname.startsWith('/search')) {
    return <MobileSearchHeader />;
  } else if (pathname.startsWith('/me/calendar')) {
    return <MobileCalendarHeader />;
  } else {
    return <DefaultToolbar />;
  }
};
